import { Component, ViewChild } from '@angular/core';
import { LedgerPostsComponent } from 'components/accounting/ledger/ledger-posts.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { ListItemComponent, ListViewComponent } from 'components/layout/list-view/list-view.component';
import { TooltipComponent } from 'components/shared/tooltip/tooltip.component';
import { CredentialType } from 'enums/CredentialType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';

@CredentialRequired(CredentialType.EditAccountingPlanEndDate)
@Component({
	templateUrl: './change-accounting-plan-end-date.component.html',
	providers: [FormValidationService],
	imports: [Layout, TooltipComponent, DateInputComponent, ListViewComponent, ListItemComponent, LedgerPostsComponent, CurrencyPipe, TranslatePipe]
})
export class ChangeAccountingPlanEndDateComponent {

	@ViewChild('ledgerPosts') ledgerPosts?: LedgerPostsComponent;

	newEndDate?: Date;
	minEndDate?: Date;
	public today: Date;

	annualContractValue?: number;
	annualValueForRenewal?: number;
	isMismatchOfAcvAndAnnualRenewalValue: boolean = false;
	newTotalPlanValue?: number;
	subscriptionEndDatesFilter? : Date[];	

	private _accountingPlan?: AccountingPlanModel;
	private _productPlan?: ProductPlanModel;

	set accountingPlan(value: AccountingPlanModel | undefined) {
		this._accountingPlan = value;
		this.newEndDate = value?.endDate;
		this.minEndDate = value?.startDate;
		this.newTotalPlanValue = value?.totalPlanValue;
		if (!this.minEndDate || this.minEndDate < this.today)
			this.minEndDate = this.today;
		this.seedSubscriptionEndDatesFilter();
	}
	get accountingPlan() { return this._accountingPlan; }

	set productPlan(value: ProductPlanModel | undefined) {
		this._productPlan = value;
		this.seedSubscriptionEndDatesFilter();
	}
	get productPlan() { return this._productPlan; }

	private seedSubscriptionEndDatesFilter() {
		if (!this.productPlan) return;
		if (!this.productPlan.restrictEndDate) return this.subscriptionEndDatesFilter = undefined;
		// Choice of end date is restricted if plan relies on an external subscription, #17473 + #22408
		// Current day is valid and every year on the "restriction date"
		return this.subscriptionEndDatesFilter = this.dateService.nextBirthdays(new Date(), this.productPlan.restrictEndDate, 7);
	}

	updateLedgerPosts(newEndDate: Date) {
		if (!this.ledgerPosts) return;
		this.ledgerPosts.disabledPosts = this.ledgerPosts.allPosts.filter(post => post.ledgerHeaderId == undefined && post.plannedDate && this.dateService.getDate(post.plannedDate) > this.dateService.getDate(newEndDate));

		this.checkMismatchOfAcvAndAnnualRenewalValue(this.newTotalPlanValue!);
	}

	checkMismatchOfAcvAndAnnualRenewalValue(newTotalPlanValue: number) {
		if ( !this.ledgerPosts) return;

		this.newTotalPlanValue = newTotalPlanValue;
		this.annualContractValue = this.accountingService.generateAnnualContractValue(this.accountingPlan?.startDate, this.newEndDate, this.newTotalPlanValue);
		if (!this.annualContractValue) return;

		this.annualValueForRenewal = this.productPlan?.annualValueForRenewal;
		if (this.annualValueForRenewal != undefined && this.annualContractValue != undefined)
			this.isMismatchOfAcvAndAnnualRenewalValue = Math.abs(this.annualContractValue - this.annualValueForRenewal) >= 1;
	}

	changeEndDate(setAnnualValueForRenewal: boolean) {
		if (!this.newEndDate || !this.accountingPlan?.id || !this.ledgerPosts) return;
		this.accountingService.changeAccountingPlanEndDate(this.accountingPlan.id, this.newEndDate, this.ledgerPosts.totalLedgerValue).then(() => {
			if (!setAnnualValueForRenewal || !this.productPlan || !this.annualContractValue) {
				this.navigation.closeView(this);
				return;
			}
			this.productPlanService.setProductPlanAnnualValueForRenewal(this.productPlan.id!, this.annualContractValue).then(() => {
				this.navigation.closeView(this);
			});
		});
	}

	cancel() {
		this.navigation.closeView(this);
	}

	constructor(
		protected readonly accountingService: AccountingService,
		protected readonly productPlanService: ProductPlanService,
		protected readonly navigation: NavigationService,
		protected readonly dateService: DateService) {

		this.today = dateService.today();
		setTimeout(() => {
			this.ledgerPosts!.postsChanged.subscribe(() => {
				// Get the date of the latest non-editable ledger post
				this.minEndDate = this.ledgerPosts!.allPosts.filter(p => !this.ledgerPosts!.isEditable(p, true)).map(p => p.plannedDate!).sort((a, b) => b.getTime() - a.getTime())[0];

				if (!this.minEndDate || this.minEndDate < this.today)
					this.minEndDate = this.today;				
			});
		});
	}
}
